import { useNavigate } from "react-router-dom";
import { Phone, Mail, MapPin, Facebook, Instagram } from "lucide-react";

const Footer = () => {
  const navigate = useNavigate();

  const scrollToTop = (path) => {
    window.scrollTo(0, 0);
    navigate(path);
  };

  const quickLinks = [
    { name: "Home", path: "/" },
    { name: "About Us", path: "/about" },
    { name: "Destinations", path: "/destinations" },
    { name: "Packages", path: "/packages" },
    { name: "Blog", path: "/blog" },
    { name: "FAQ", path: "/faq" },
    { name: "Contact", path: "/contact" },
  ];

  const services = [
    { name: "International Tours", path: "/packages?category=international" },
    { name: "Domestic Tours", path: "/packages?category=domestic" },
    { name: "Honeymoon Packages", path: "/packages?type=honeymoon" },
    { name: "Weddingz by DNO", path: "/weddingz" },
    { name: "Corporate Travel", path: "/corporate" },
    { name: "Group Tours", path: "/packages?type=group" },
  ];

  const legal = [
    { name: "Privacy Policy", path: "/privacy" },
    { name: "Terms & Conditions", path: "/terms" },
    { name: "Cancellation Policy", path: "/cancellation" },
  ];

  return (
    <footer className="bg-slate-50 border-t border-slate-200" data-testid="main-footer">
      {/* Main Footer */}
      <div className="container-custom py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Section */}
          <div className="lg:col-span-1">
            <button onClick={() => scrollToTop("/")} className="inline-block mb-6" data-testid="footer-logo">
              <div className="flex items-center gap-2">
                <img 
                  src="/assets/images/logo.png" 
                  alt="Dnotours Logo" 
                  className="h-10 w-auto"
                />
              </div>
            </button>
            <p className="text-slate-600 mb-6 leading-relaxed">
              Your trusted partner for premium travel experiences. Crafting unforgettable journeys since 2017.
            </p>
            <div className="flex gap-4">
              <a
                href="https://www.facebook.com/dnotours"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-coral hover:text-white hover:border-coral transition-all"
                data-testid="social-facebook"
              >
                <Facebook className="h-5 w-5" />
              </a>
              <a
                href="https://www.instagram.com/dnotours"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-coral hover:text-white hover:border-coral transition-all"
                data-testid="social-instagram"
              >
                <Instagram className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-heading font-semibold mb-6 text-slate-800">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <button
                    onClick={() => scrollToTop(link.path)}
                    className="text-slate-600 hover:text-coral transition-colors text-left"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-heading font-semibold mb-6 text-slate-800">Our Services</h4>
            <ul className="space-y-3">
              {services.map((link) => (
                <li key={link.name}>
                  <button
                    onClick={() => scrollToTop(link.path)}
                    className="text-slate-600 hover:text-coral transition-colors text-left"
                    data-testid={`footer-service-${link.name.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-heading font-semibold mb-6 text-slate-800">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-coral flex-shrink-0 mt-1" />
                <span className="text-slate-600">
                  Office 02, Sairaj Gardens,<br />
                  Iraniwadi Rd Number 3,<br />
                  Kandivali West, Mumbai 400067
                </span>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-coral flex-shrink-0 mt-1" />
                <span className="text-slate-600">
                  <strong className="text-slate-700">Registered Office:</strong><br />
                  804, A Wing, Veena Classic,<br />
                  Modi Park, Irani Wadi Road No. 3,<br />
                  Kandivali West, Mumbai 400067
                </span>
              </li>
              <li>
                <a
                  href="tel:+918369033791"
                  className="flex items-center gap-3 text-slate-600 hover:text-coral transition-colors"
                  data-testid="footer-phone"
                >
                  <Phone className="h-5 w-5 text-coral flex-shrink-0" />
                  +91 8369033791
                </a>
              </li>
              <li>
                <a
                  href="mailto:info@dnotours.com"
                  className="flex items-center gap-3 text-slate-600 hover:text-coral transition-colors"
                  data-testid="footer-email"
                >
                  <Mail className="h-5 w-5 text-coral flex-shrink-0" />
                  info@dnotours.com
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-200 bg-white">
        <div className="container-custom py-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-slate-500 text-sm text-center md:text-left">
            © {new Date().getFullYear()} DNO Events & DMC Private Limited. All rights reserved.
          </p>
          <div className="flex flex-wrap justify-center gap-6">
            {legal.map((link) => (
              <button
                key={link.name}
                onClick={() => scrollToTop(link.path)}
                className="text-sm text-slate-500 hover:text-coral transition-colors"
                data-testid={`footer-legal-${link.name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {link.name}
              </button>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
